import React, { useState, useCallback, useEffect, useImperativeHandle, forwardRef } from "react";
import {
    Button,
    Grid,
    TextField,
    Typography,
    Paper,
    IconButton,
    Divider,
    Switch,
    FormControlLabel,
    Box,
    CircularProgress,
    Snackbar,
    Tooltip,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import SaveIcon from "@material-ui/icons/Save";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import jsPDF from "jspdf";
import firebase from "firebase/app";
import "firebase/database";

const LOGO_URL =
    "https://firebasestorage.googleapis.com/v0/b/kmetasfirebase.appspot.com/o/fotosVariasEvento%2Fevento_572%2F12044-DASHPORT_color_horizontal.png?alt=media&token=ebc7289d-e77e-431e-97ba-db40f73be9be";

const ACCENT = [216, 90, 48];

const mxn = (n) =>
    "$" +
    Number(n).toLocaleString("es-MX", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const SECCIONES_DEFAULT = [
    {
        titulo: "Plataforma de Inscripciones",
        items: [
            {
                desc: "Módulo para cada punto de venta, usuarios ilimitados.\nMonitoreo en tiempo real con estadísticas relevantes.\nInscripciones en línea con pago web o en tiendas de conveniencia (7% comisión bancaria).\nPosibilidad de comprar paquetes o servicios adicionales (carrito de compras).",
                cant: 1,
                precio: 1800,
            },
        ],
    },
    {
        titulo: "Cronometraje",
        items: [
            { desc: "Punto de control salida y meta con Lector", cant: 1, precio: 3000 },
            { desc: "Chips desechables", cant: 300, precio: 28 },
            { desc: "Software de crono con opción de respaldo en puntos relevantes de la ruta", cant: 1, precio: 1800 },
            { desc: "Coordinación y ejecución", cant: 1, precio: 2000 },
            { desc: "Staff para el evento", cant: 5, precio: 500 },
            { desc: "Traslados y viáticos", cant: 1, precio: 600 },
            { desc: "Reloj LED para la meta", cant: 1, precio: 1000 },
        ],
    },
    {
        titulo: "Fotos",
        items: [{ desc: "Fotografía en meta de cada corredor de acuerdo a su tiempo\nLa fotografía puede llevar branding del evento (cintillo o marco)", cant: 1, precio: 1500 }],
    },
];

const CONSIDERACIONES_DEFAULT = [
    "Atletas adicionales o inferiores ajustados a razón de 28 MXN por chip.",
    "Servicio de fotografía opcional incluido en la cotización por un valor de $1,500 MXN.",
];

// ── Helper para mover elementos en un array ──────────────────────
const moverEn = (arr, i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const n = [...arr];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
};

// ── Genera número de cotización alfanumérico de 6 caracteres ───
const generarNumeroCot = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // sin O,0,1,I para evitar confusiones
    return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

const Cotizacion = forwardRef(({ cotizacionId = null, eventoId = null, onGuardado, hideActions = false }, ref) => {
    const [cliente, setCliente] = useState("");
    const [evento, setEvento] = useState("");
    const [lugar, setLugar] = useState("Mérida, Yucatán");
    const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
    const [participantes, setParticipantes] = useState(0);
    const [secciones, setSecciones] = useState(SECCIONES_DEFAULT);
    const [consideraciones, setConsideraciones] = useState(CONSIDERACIONES_DEFAULT);
    const [mostrarDesglose, setMostrarDesglose] = useState(false);
    const [vinculoEventoId, setVinculoEventoId] = useState(eventoId || "");
    const [numeroCot, setNumeroCot] = useState(() => generarNumeroCot());
    const [notas, setNotas] = useState("");
    const [guardando, setGuardando] = useState(false);
    const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

    // ── Cargar cotización existente ───────────────────────────────
    useEffect(() => {
        if (!cotizacionId) return;
        const dbRef = firebase.database().ref(`cotizaciones/${cotizacionId}`);
        dbRef.once("value").then((snap) => {
            const d = snap.val();
            if (!d) return;
            setCliente(d.cliente || "");
            setEvento(d.evento || "");
            setLugar(d.lugar || "Mérida, Yucatán");
            setFecha(d.fecha || new Date().toISOString().slice(0, 10));
            setParticipantes(d.participantes || 0);
            setSecciones(d.secciones || SECCIONES_DEFAULT);
            setConsideraciones(d.consideraciones || CONSIDERACIONES_DEFAULT);
            setVinculoEventoId(d.eventoId || "");
            setNumeroCot(d.numeroCot || generarNumeroCot());
            setNotas(d.notas || "");
        });
    }, [cotizacionId]);

    // ── Totales ───────────────────────────────────────────────────
    const subtotal = secciones.reduce(
        (a, s) => a + s.items.reduce((b, it) => b + it.cant * it.precio, 0),
        0
    );
    const iva = subtotal * 0.16;
    const total = subtotal + iva;

    // ── Secciones ─────────────────────────────────────────────────
    const updateSecTitulo = (si, val) =>
        setSecciones((prev) => prev.map((s, i) => (i === si ? { ...s, titulo: val } : s)));

    const deleteSeccion = (si) =>
        setSecciones((prev) => prev.filter((_, i) => i !== si));

    const addSeccion = () =>
        setSecciones((prev) => [
            ...prev,
            { titulo: "Nueva sección", items: [{ desc: "Nuevo ítem", cant: 1, precio: 0 }] },
        ]);

    const moverSeccion = (si, dir) =>
        setSecciones((prev) => moverEn(prev, si, dir));

    // ── Ítems ─────────────────────────────────────────────────────
    const updateItem = useCallback((si, ii, field, val) => {
        setSecciones((prev) =>
            prev.map((s, sIdx) =>
                sIdx !== si
                    ? s
                    : {
                        ...s,
                        items: s.items.map((it, iIdx) =>
                            iIdx !== ii
                                ? it
                                : { ...it, [field]: field === "desc" ? val : Number(val) }
                        ),
                    }
            )
        );
    }, []);

    const addItem = (si) =>
        setSecciones((prev) =>
            prev.map((s, i) =>
                i !== si ? s : { ...s, items: [...s.items, { desc: "Nuevo ítem", cant: 1, precio: 0 }] }
            )
        );

    const deleteItem = (si, ii) =>
        setSecciones((prev) =>
            prev.map((s, i) =>
                i !== si ? s : { ...s, items: s.items.filter((_, j) => j !== ii) }
            )
        );

    const moverItem = (si, ii, dir) =>
        setSecciones((prev) =>
            prev.map((s, i) =>
                i !== si ? s : { ...s, items: moverEn(s.items, ii, dir) }
            )
        );

    // ── Consideraciones ───────────────────────────────────────────
    const updateCons = (i, val) =>
        setConsideraciones((prev) => prev.map((c, idx) => (idx === i ? val : c)));

    const deleteCons = (i) =>
        setConsideraciones((prev) => prev.filter((_, idx) => idx !== i));

    const addCons = () =>
        setConsideraciones((prev) => [...prev, "Nueva consideración"]);

    // ── Guardar en Firebase ───────────────────────────────────────
    const guardar = useCallback(async () => {
        setGuardando(true);
        try {
            const data = {
                cliente, evento, lugar, fecha, participantes,
                secciones, consideraciones,
                eventoId: vinculoEventoId || null,
                numeroCot,
                notas: notas || null,
                subtotal, iva, total,
                updatedAt: firebase.database.ServerValue.TIMESTAMP,
            };
            const dbRef = firebase.database().ref("cotizaciones");
            if (cotizacionId) {
                await dbRef.child(cotizacionId).update(data);
                setSnack({ open: true, msg: "Cotización actualizada", severity: "success" });
                onGuardado && onGuardado(cotizacionId);
            } else {
                data.createdAt = firebase.database.ServerValue.TIMESTAMP;
                const newRef = await dbRef.push(data);
                setSnack({ open: true, msg: "Cotización guardada", severity: "success" });
                onGuardado && onGuardado(newRef.key);
            }
        } catch (err) {
            console.error(err);
            setSnack({ open: true, msg: "Error al guardar: " + err.message, severity: "error" });
        } finally {
            setGuardando(false);
        }
    }, [cliente, evento, lugar, fecha, participantes, secciones, consideraciones, vinculoEventoId, numeroCot, notas, subtotal, iva, total, cotizacionId, onGuardado]);

    // ── Generar PDF ───────────────────────────────────────────────
    const generarPDF = useCallback(() => {
        const fechaEvento = fecha
            ? new Date(fecha + "T12:00:00").toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })
            : new Date().toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });

        const fechaHoy = new Date().toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });

        const buildPDF = (logoDataUrl, imgEl) => {
            const doc = new jsPDF({ unit: "mm", format: "a4" });
            const PW = doc.internal.pageSize.width;
            const PH = doc.internal.pageSize.height;
            let y = 20;

            const chk = (needed) => {
                if (y + needed > PH - 28) { doc.addPage(); y = 20; }
            };

            if (logoDataUrl && imgEl) {
                const logoW = 48;
                const logoH = logoW * (imgEl.naturalHeight / imgEl.naturalWidth);
                doc.addImage(logoDataUrl, "PNG", 14, y, logoW, logoH);
                doc.setFontSize(10);
                doc.setFont(undefined, "normal");
                doc.setTextColor(110, 110, 110);
                doc.text(`${fechaHoy}`, PW - 14, y + logoH / 2 + 1, { align: "right" });
                y += logoH + 8;
            } else {
                doc.setFontSize(18);
                doc.setFont(undefined, "bold");
                doc.setTextColor(...ACCENT);
                doc.text("dashport", 14, y + 10);
                doc.setFontSize(10);
                doc.setFont(undefined, "normal");
                doc.setTextColor(110, 110, 110);
                doc.text(`${lugar}, ${fechaHoy}`, PW - 14, y + 10, { align: "right" });
                y += 18;
            }

            doc.setDrawColor(...ACCENT);
            doc.setLineWidth(0.5);
            doc.line(14, y, PW - 14, y);
            y += 9;

            doc.setFontSize(12);
            doc.setFont(undefined, "bold");
            doc.setTextColor(30, 30, 30);
            doc.text(cliente || "Cliente", 14, y);
            doc.setFontSize(9);
            doc.setFont(undefined, "normal");
            doc.setTextColor(160, 160, 160);
            doc.text(`Cot. #${numeroCot}`, PW - 14, y, { align: "right" });
            y += 7;

            doc.setFontSize(10);
            doc.setFont(undefined, "normal");
            doc.setTextColor(80, 80, 80);
            const intro = doc.splitTextToSize("Por medio de la presente, Dashport le hace llegar la cotización de servicio:", PW - 28);
            doc.text(intro, 14, y);
            y += intro.length * 5 + 8;

            doc.setFont(undefined, "bold");
            doc.setTextColor(50, 50, 50);
            doc.text("Generales", 14, y);
            y += 6;
            doc.setFont(undefined, "normal");
            doc.setTextColor(70, 70, 70);
            doc.text(`Fecha del evento: ${fechaEvento}`, 14, y); y += 5;
            doc.text(`Lugar: ${lugar}`, 14, y); y += 5;
            doc.text(`Evento: ${evento || "—"}`, 14, y); y += 5;
            doc.text(`Participantes: ${participantes} pax`, 14, y); y += 10;

            doc.setDrawColor(...ACCENT);
            doc.line(14, y, PW - 14, y);
            y += 8;

            secciones.forEach((sec, si) => {
                chk(14);
                doc.setFontSize(11);
                doc.setFont(undefined, "bold");
                doc.setTextColor(...ACCENT);
                doc.text(`${si + 1}. ${sec.titulo}`, 14, y);
                y += 6;
                doc.setFont(undefined, "normal");
                doc.setTextColor(60, 60, 60);

                sec.items.forEach((it) => {
                    const wrap = mostrarDesglose ? 145 : 178;
                    const lines = doc.splitTextToSize(it.desc, wrap);
                    chk(lines.length * 5 + (mostrarDesglose ? 8 : 3));
                    doc.setFontSize(10);
                    doc.text(lines, 20, y);
                    y += lines.length * 5;
                    if (mostrarDesglose) {
                        doc.setFontSize(9);
                        doc.setTextColor(120, 120, 120);
                        doc.text(`Cant: ${it.cant}  ·  Precio unit.: ${mxn(it.precio)}  ·  Total: ${mxn(it.cant * it.precio)}`, 20, y);
                        doc.setTextColor(60, 60, 60);
                        doc.setFontSize(10);
                        y += 6;
                    } else {
                        y += 3;
                    }
                });
                y += 4;
            });

            chk(30);
            doc.setDrawColor(...ACCENT);
            doc.line(14, y, PW - 14, y);
            y += 8;

            doc.setFontSize(11);
            doc.setFont(undefined, "bold");
            doc.setTextColor(50, 50, 50);
            doc.text("Inversión", 14, y);
            y += 7;

            doc.setFontSize(10);
            doc.setFont(undefined, "normal");
            doc.setTextColor(80, 80, 80);
            doc.text("Subtotal:", 120, y); doc.text(mxn(subtotal), PW - 14, y, { align: "right" }); y += 5;
            doc.text("IVA (16%):", 120, y); doc.text(mxn(iva), PW - 14, y, { align: "right" }); y += 6;

            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.3);
            doc.line(120, y, PW - 14, y);
            y += 5;

            doc.setFont(undefined, "bold");
            doc.setFontSize(11);
            doc.setTextColor(30, 30, 30);
            doc.text("Total:", 120, y);
            doc.setTextColor(...ACCENT);
            doc.text(mxn(total), PW - 14, y, { align: "right" });
            y += 12;

            if (consideraciones.length > 0) {
                chk(16);
                doc.setFontSize(10);
                doc.setFont(undefined, "bold");
                doc.setTextColor(50, 50, 50);
                doc.text("Consideraciones:", 14, y);
                y += 6;
                doc.setFont(undefined, "normal");
                doc.setTextColor(70, 70, 70);
                consideraciones.forEach((c, i) => {
                    const lines = doc.splitTextToSize(`${i + 1}. ${c}`, 178);
                    chk(lines.length * 5 + 3);
                    doc.text(lines, 14, y);
                    y += lines.length * 5 + 2;
                });
                y += 4;
            }

            chk(18);
            doc.setFontSize(10);
            doc.setFont(undefined, "normal");
            doc.setTextColor(80, 80, 80);
            doc.text("Cualquier duda o comentario quedamos a sus órdenes.", 14, y);
            y += 10;

            doc.setDrawColor(...ACCENT);
            doc.line(14, y, PW - 14, y);
            y += 5;
            doc.setFontSize(9);
            doc.setTextColor(120, 120, 120);
            doc.text("Dashport · Eduardo Madrid Alvarez de Lugo", 14, y); y += 4;
            doc.text("Tel: 999 740.50.97  ·  Email: eduardo@dashport.run", 14, y); y += 4;
            doc.text("RFC: DAS171012PC8  ·  Mérida, Yucatán, México", 14, y);

            const fechaHoyCorta = new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\//g, "-");
            const titulosSecciones = secciones.map((s) => s.titulo).join(", ");
            const nombreArchivo = `Cotización servicio ${titulosSecciones} - ${evento || "Sin evento"} - ${fechaHoyCorta}`;
            doc.save(`${nombreArchivo}.pdf`);
        };

        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext("2d").drawImage(img, 0, 0);
            buildPDF(canvas.toDataURL("image/png"), img);
        };
        img.onerror = () => buildPDF(null, null);
        img.src = LOGO_URL;
    }, [cliente, evento, lugar, fecha, participantes, secciones, consideraciones, mostrarDesglose, subtotal, iva, total]);

    // ── Exponer métodos al padre vía ref ──────────────────────────
    useImperativeHandle(ref, () => ({
        guardar,
        generarPDF,
        guardando,
    }), [guardar, generarPDF, guardando]);

    // ── Estilos compartidos para botones de orden ─────────────────
    const btnOrden = { padding: 4 };

    // ── Render ────────────────────────────────────────────────────
    return (
        <Box>
            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <MuiAlert severity={snack.severity} elevation={6} variant="filled">
                    {snack.msg}
                </MuiAlert>
            </Snackbar>

            {/* Datos generales */}
            <Paper variant="outlined" style={{ padding: 16, marginBottom: 20 }}>
                <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>
                    Datos del evento
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Cliente" fullWidth value={cliente} onChange={(e) => setCliente(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Evento" fullWidth value={evento} onChange={(e) => setEvento(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Lugar" fullWidth value={lugar} onChange={(e) => setLugar(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Fecha"
                            type="date"
                            fullWidth
                            value={fecha}
                            onChange={(e) => setFecha(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Participantes"
                            type="number"
                            fullWidth
                            value={participantes}
                            onChange={(e) => setParticipantes(Number(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Vincular ID de evento (opcional)"
                            fullWidth
                            value={vinculoEventoId}
                            onChange={(e) => setVinculoEventoId(e.target.value)}
                            placeholder="ej. evento_123"
                            helperText="Deja vacío si no aplica"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="N° cotización"
                            fullWidth
                            value={numeroCot}
                            InputProps={{ readOnly: true }}
                            helperText="Generado automáticamente"
                        />
                    </Grid>
                    <Grid item xs={12} sm={9}>
                        <TextField
                            label="Notas internas (no aparece en el PDF)"
                            fullWidth
                            multiline
                            rows={2}
                            value={notas}
                            onChange={(e) => setNotas(e.target.value)}
                            placeholder="Observaciones, condiciones especiales, seguimiento..."
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Secciones */}
            <Typography variant="h6" gutterBottom>
                Secciones y servicios
            </Typography>

            {secciones.map((sec, si) => (
                <Paper
                    key={si}
                    variant="outlined"
                    style={{ padding: 16, marginBottom: 16, backgroundColor: "#fafafa" }}
                >
                    {/* Header de sección */}
                    <Grid container spacing={1} alignItems="center" style={{ marginBottom: 8 }}>
                        {/* Controles de orden de sección */}
                        <Grid item style={{ display: "flex", flexDirection: "column", paddingRight: 4 }}>
                            <Tooltip title="Subir sección">
                                <span>
                                    <IconButton size="small" style={btnOrden} onClick={() => moverSeccion(si, -1)} disabled={si === 0}>
                                        <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Bajar sección">
                                <span>
                                    <IconButton size="small" style={btnOrden} onClick={() => moverSeccion(si, 1)} disabled={si === secciones.length - 1}>
                                        <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Grid>
                        <Grid item xs>
                            <TextField
                                label="Título de sección"
                                fullWidth
                                value={sec.titulo}
                                onChange={(e) => updateSecTitulo(si, e.target.value)}
                            />
                        </Grid>
                        <Grid item>
                            <Tooltip title="Eliminar sección">
                                <IconButton onClick={() => deleteSeccion(si)} size="small">
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>

                    {/* Encabezado columnas */}
                    <Grid container spacing={1} alignItems="center" style={{ padding: "4px 8px", marginBottom: 2 }}>
                        <Grid item style={{ width: 52 }} />
                        <Grid item xs={5}>
                            <Typography variant="caption" style={{ color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Descripción</Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="caption" style={{ color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Cantidad</Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="caption" style={{ color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Precio unit.</Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <Typography variant="caption" style={{ color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Total</Typography>
                        </Grid>
                        <Grid item xs={1} />
                    </Grid>

                    {/* Ítems */}
                    {sec.items.map((item, ii) => (
                        <Grid
                            key={ii}
                            container
                            spacing={1}
                            alignItems="center"
                            style={{
                                padding: "4px 8px",
                                marginBottom: 4,
                                backgroundColor: ii % 2 === 0 ? "#fff" : "#f7f7f7",
                                border: "1px solid #e8e8e8",
                                borderRadius: 4,
                            }}
                        >
                            {/* Controles de orden de ítem */}
                            <Grid item style={{ display: "flex", flexDirection: "column", width: 52, flexShrink: 0 }}>
                                <Tooltip title="Subir ítem">
                                    <span>
                                        <IconButton size="small" style={btnOrden} onClick={() => moverItem(si, ii, -1)} disabled={ii === 0}>
                                            <ArrowUpwardIcon style={{ fontSize: 14 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Bajar ítem">
                                    <span>
                                        <IconButton size="small" style={btnOrden} onClick={() => moverItem(si, ii, 1)} disabled={ii === sec.items.length - 1}>
                                            <ArrowDownwardIcon style={{ fontSize: 14 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={5}>
                                <TextField
                                    fullWidth
                                    multiline
                                    maxRows={3}
                                    value={item.desc}
                                    onChange={(e) => updateItem(si, ii, "desc", e.target.value)}
                                    variant="standard"
                                    InputProps={{ disableUnderline: false, style: { fontSize: 13 } }}
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    type="number"
                                    fullWidth
                                    value={item.cant}
                                    onChange={(e) => updateItem(si, ii, "cant", e.target.value)}
                                    variant="standard"
                                    InputProps={{ disableUnderline: false, style: { fontSize: 13 } }}
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    type="number"
                                    fullWidth
                                    value={item.precio}
                                    onChange={(e) => updateItem(si, ii, "precio", e.target.value)}
                                    variant="standard"
                                    InputProps={{ disableUnderline: false, style: { fontSize: 13 } }}
                                />
                            </Grid>
                            <Grid item xs={1}>
                                <Typography variant="body2" style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>
                                    {mxn(item.cant * item.precio)}
                                </Typography>
                            </Grid>
                            <Grid item xs={1} style={{ textAlign: "right" }}>
                                <Tooltip title="Eliminar ítem">
                                    <IconButton onClick={() => deleteItem(si, ii)} size="small">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    ))}

                    <Button startIcon={<AddIcon />} size="small" variant="outlined" onClick={() => addItem(si)} style={{ marginTop: 8 }}>
                        Agregar ítem
                    </Button>
                </Paper>
            ))}

            <Button startIcon={<AddIcon />} variant="outlined" onClick={addSeccion} style={{ marginBottom: 24 }}>
                Agregar sección
            </Button>

            {/* Consideraciones */}
            <Typography variant="h6" gutterBottom>
                Consideraciones adicionales
            </Typography>

            {consideraciones.map((c, i) => (
                <Paper key={i} variant="outlined" style={{ padding: 12, marginBottom: 8 }}>
                    <Grid container spacing={1} alignItems="flex-start">
                        <Grid item xs={11}>
                            <TextField fullWidth multiline rows={2} value={c} onChange={(e) => updateCons(i, e.target.value)} />
                        </Grid>
                        <Grid item xs={1}>
                            <IconButton onClick={() => deleteCons(i)} size="small">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Paper>
            ))}

            <Button startIcon={<AddIcon />} size="small" variant="outlined" onClick={addCons} style={{ marginBottom: 24 }}>
                Agregar consideración
            </Button>

            {/* Totales */}
            <Paper variant="outlined" style={{ padding: 16, marginBottom: 24, maxWidth: 340 }}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="textSecondary">Subtotal</Typography>
                    <Typography variant="body2">{mxn(subtotal)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="textSecondary">IVA (16%)</Typography>
                    <Typography variant="body2">{mxn(iva)}</Typography>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Total</Typography>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{mxn(total)}</Typography>
                </Box>
            </Paper>

            {/* Opciones PDF */}
            <Box mb={3}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={mostrarDesglose}
                            onChange={(e) => setMostrarDesglose(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Mostrar desglose de precios por ítem en el PDF"
                />
            </Box>

            {/* Acciones — solo si no las maneja el padre */}
            {!hideActions && (
                <Grid container spacing={2}>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                            onClick={guardar}
                            disabled={guardando}
                        >
                            {cotizacionId ? "Actualizar cotización" : "Guardar cotización"}
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={generarPDF}
                        >
                            Generar PDF
                        </Button>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
});

Cotizacion.displayName = "Cotizacion";

export default Cotizacion;